import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Layout, Typography, notification, Input, Tooltip, Modal } from 'antd';
import moment from 'moment';
import axios from 'axios';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import IncidentModal from './IncidentModal';
import LoginRequired from './LoginRequired';
import { isResolved } from './statusMeta';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'moment/locale/sk';
import './style.css';

moment.locale('sk');
moment.updateLocale('sk', { week: { dow: 1, doy: 4 } });

const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(Calendar);

const ACTIVITY = {
  requested: {
    key: 'requested',
    label: 'Požadované',
    color: '#5abbff',
    bg: 'linear-gradient(135deg, #5abbff 0%, #2a6a99 100%)',
  },
  resolved: {
    key: 'resolved',
    label: 'Vyriešené',
    color: '#52c41a',
    bg: 'linear-gradient(135deg, #52c41a 0%, #2a6a1a 100%)',
  },
};

const toValidDate = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.toDate() : null;
};

const eventEnd = (start) => moment(start).add(1, 'minute').toDate();

const isResolvedStatus = (status) => isResolved(status);

const eventsOnDay = (list, date) => {
  const day = moment(date);
  return (list || [])
    .filter((e) => moment(e.start).isSame(day, 'day'))
    .sort((a, b) => moment(a.start).valueOf() - moment(b.start).valueOf());
};

const getViewRange = (date, view) => {
  const m = moment(date);
  if (view === 'day') {
    return {
      start: m.clone().startOf('day'),
      end: m.clone().endOf('day'),
      label: m.format('D. M. YYYY'),
    };
  }
  if (view === 'week') {
    const start = m.clone().startOf('isoWeek');
    const end = m.clone().endOf('isoWeek');
    return {
      start,
      end,
      label: `${start.format('D. M.')} – ${end.format('D. M. YYYY')}`,
    };
  }
  const start = m.clone().startOf('month');
  const end = m.clone().endOf('month');
  return {
    start,
    end,
    label: m.format('MMMM YYYY'),
  };
};

const countByType = (list) => ({
  requested: list.filter((e) => e.activityType === 'requested').length,
  resolved: list.filter((e) => e.activityType === 'resolved').length,
});

const mapIncidentsToEvents = (incidents) => {
  const events = [];
  (incidents || []).forEach((incident) => {
    const createdAt = toValidDate(incident.created);
    const resolvedAt =
      toValidDate(incident.resolvedAt) ||
      toValidDate(incident.completionDate) ||
      toValidDate(incident.lastUpdate);

    if (createdAt) {
      events.push({
        id: `req-${incident.id}`,
        incidentId: incident.id,
        niceid: incident.niceid,
        text: incident.text,
        title: `＋ ${incident.niceid}: ${incident.text}`,
        start: createdAt,
        end: eventEnd(createdAt),
        activityType: 'requested',
        status: incident.status,
      });
    }

    if (isResolvedStatus(incident.status) && resolvedAt) {
      events.push({
        id: `res-${incident.id}`,
        incidentId: incident.id,
        niceid: incident.niceid,
        text: incident.text,
        title: `✓ ${incident.niceid}: ${incident.text}`,
        start: resolvedAt,
        end: eventEnd(resolvedAt),
        activityType: 'resolved',
        status: incident.status,
      });
    }
  });
  return events;
};

const CalendarEvent = ({ event }) => {
  const meta = ACTIVITY[event.activityType] || ACTIVITY.requested;
  return (
    <Tooltip
      title={
        <div className="cal-tip">
          <div className="cal-tip-type" style={{ color: meta.color }}>{meta.label}</div>
          <div className="cal-tip-id">{event.niceid}</div>
          <div className="cal-tip-text">{event.text}</div>
          <div className="cal-tip-time">{moment(event.start).format('D. M. YYYY HH:mm')}</div>
        </div>
      }
      color="#1e1f22"
      mouseEnterDelay={0.15}
    >
      <div className={`cal-event cal-event--${event.activityType}`}>
        <span className="cal-event-mark" aria-hidden>
          {event.activityType === 'resolved' ? '✓' : '＋'}
        </span>
        <span className="cal-event-id">{event.niceid}</span>
        <span className="cal-event-text">{event.text}</span>
      </div>
    </Tooltip>
  );
};

const CalendarLegend = ({ total, period, periodLabel }) => (
  <div className="cal-legend fade-in">
    <div className="cal-legend-period">Obdobie: {periodLabel}</div>
    <div className="cal-legend-items">
      <div className="cal-legend-item">
        <span className="cal-legend-swatch requested" />
        <span>Požadované</span>
        <span className="cal-legend-nums">
          <strong style={{ color: ACTIVITY.requested.color }}>{period.requested}</strong>
          <span className="cal-legend-total">/ {total.requested} celkom</span>
        </span>
      </div>
      <div className="cal-legend-item">
        <span className="cal-legend-swatch resolved" />
        <span>Vyriešené</span>
        <span className="cal-legend-nums">
          <strong style={{ color: ACTIVITY.resolved.color }}>{period.resolved}</strong>
          <span className="cal-legend-total">/ {total.resolved} celkom</span>
        </span>
      </div>
      <div className="cal-legend-item cal-legend-weekend">
        <span className="cal-legend-swatch weekend" />
        <span>Víkend (So / Ne)</span>
      </div>
    </div>
  </div>
);

const CustomToolbar = (toolbar) => {
  const goToBack = () => toolbar.onNavigate('PREV');
  const goToNext = () => toolbar.onNavigate('NEXT');
  const goToToday = () => toolbar.onNavigate('TODAY');
  const label = moment(toolbar.date).format('MMMM YYYY');

  return (
    <div className="rbc-toolbar cal-toolbar">
      <span className="rbc-btn-group">
        <button type="button" onClick={goToBack}>Späť</button>
        <button type="button" onClick={goToToday}>Dnes</button>
        <button type="button" onClick={goToNext}>Ďalej</button>
      </span>
      <span className="rbc-toolbar-label cal-toolbar-label">{label}</span>
      <span className="rbc-btn-group">
        <button
          type="button"
          className={toolbar.view === 'month' ? 'rbc-active' : ''}
          onClick={() => toolbar.onView('month')}
        >
          Mesiac
        </button>
        <button
          type="button"
          className={toolbar.view === 'week' ? 'rbc-active' : ''}
          onClick={() => toolbar.onView('week')}
        >
          Týždeň
        </button>
        <button
          type="button"
          className={toolbar.view === 'day' ? 'rbc-active' : ''}
          onClick={() => toolbar.onView('day')}
        >
          Deň
        </button>
      </span>
    </div>
  );
};

const Dashboard = ({ loggedInUser }) => {
  const [events, setEvents] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [dragAndDropEnabled, setDragAndDropEnabled] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [calDate, setCalDate] = useState(() => new Date());
  const [calView, setCalView] = useState('month');
  const [dayModal, setDayModal] = useState({ open: false, date: null, events: [] });

  const fetchEvents = useCallback(() => {
    setLoading(true);
    axios.get(`${process.env.REACT_APP_API_URL}/events`)
      .then((response) => {
        setEvents(mapIncidentsToEvents(response.data));
      })
      .catch((error) => {
        console.error('Chyba pri načítaní udalostí pre kalendár:', error);
        notification.error({
          message: 'Chyba',
          description: 'Nastala chyba pri načítaní udalostí pre kalendár.',
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (loggedInUser) fetchEvents();
  }, [loggedInUser, fetchEvents]);

  const totalCounts = useMemo(() => countByType(events), [events]);

  const viewRange = useMemo(() => getViewRange(calDate, calView), [calDate, calView]);

  const periodCounts = useMemo(() => {
    const inPeriod = events.filter((e) =>
      moment(e.start).isBetween(viewRange.start, viewRange.end, null, '[]'),
    );
    return countByType(inPeriod);
  }, [events, viewRange]);

  const handleEventClick = (event) => {
    const incidentId = event.incidentId || event.id;
    axios.get(`${process.env.REACT_APP_API_URL}/incident/get/${incidentId}`)
      .then((response) => {
        setDayModal({ open: false, date: null, events: [] });
        setSelectedIncident(response.data);
        setModalOpen(true);
      })
      .catch((error) => {
        console.error('Chyba pri načítaní incidentu:', error);
      });
  };

  const handleShowMore = (slotEvents, date) => {
    const dayEvents = eventsOnDay(events, date);
    setDayModal({
      open: true,
      date,
      events: dayEvents.length ? dayEvents : eventsOnDay(slotEvents, date),
    });
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedIncident(null);
  };

  const handleEventDrop = ({ event, end }) => {
    if (!dragAndDropEnabled) return;

    const incidentId = event.incidentId || event.id;
    const updatedDueDate = moment(end).format('YYYY-MM-DD HH:mm:ss');
    axios.post(`${process.env.REACT_APP_API_URL}/incident/update`, {
      id: incidentId,
      dueDate: updatedDueDate,
    })
      .then(() => {
        notification.success({
          message: 'Incident aktualizovaný',
          description: 'Termín splnenia bol úspešne aktualizovaný.',
        });
        fetchEvents();
      })
      .catch((error) => {
        console.error('Chyba pri aktualizácii termínu splnenia:', error);
        notification.error({
          message: 'Chyba',
          description: 'Pri aktualizácii termínu splnenia došlo k chybe.',
        });
      });
  };

  useEffect(() => {
    if (password === 'sombrero123') {
      setDragAndDropEnabled(true);
      notification.success({
        message: 'Odomknuté',
        description: 'Drag-and-drop bol povolený.',
      });
    } else {
      setDragAndDropEnabled(false);
    }
  }, [password]);

  const dayPropGetter = useCallback((date) => {
    const dow = moment(date).day();
    const isWeekend = dow === 0 || dow === 6;
    const isToday = moment(date).isSame(moment(), 'day');
    return {
      className: [
        isWeekend ? 'rbc-day-weekend' : 'rbc-day-weekday',
        isToday ? 'rbc-day-is-today' : '',
      ].filter(Boolean).join(' '),
    };
  }, []);

  const eventPropGetter = useCallback((event) => {
    const meta = ACTIVITY[event.activityType] || ACTIVITY.requested;
    return {
      className: `cal-rbc-event cal-rbc-event--${event.activityType}`,
      style: {
        background: meta.bg,
        borderColor: meta.color,
        color: '#fff',
      },
    };
  }, []);

  if (!loggedInUser) {
    return (
      <Layout style={{ padding: '24px 16px', minHeight: 360 }}>
        <LoginRequired
          title="Prihláste sa pre zobrazenie kalendára"
          subtitle="Kalendár udalostí a termínov je dostupný až po prihlásení firemným účtom."
        />
      </Layout>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <Layout className="calendar-page">
        <div className="cal-header">
          <Typography.Title level={4} className="white cal-title">
            Kalendár aktivity
          </Typography.Title>
        </div>

        <div className={`cal-shell${loading ? ' is-loading' : ''}`}>
          <DragAndDropCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            titleAccessor="title"
            eventPropGetter={eventPropGetter}
            dayPropGetter={dayPropGetter}
            style={{ height: 800 }}
            views={['month', 'week', 'day']}
            defaultView="month"
            date={calDate}
            view={calView}
            onNavigate={(date, view) => {
              setCalDate(date);
              if (view) setCalView(view);
            }}
            onView={setCalView}
            popup={false}
            doShowMoreDrillDown={false}
            onShowMore={handleShowMore}
            messages={{
              showMore: (total) => `+${total} ďalších`,
            }}
            components={{
              event: CalendarEvent,
              timeGutterHeader: () => <div className="cal-time-gutter-label">Čas</div>,
              toolbar: CustomToolbar,
            }}
            formats={{
              timeGutterFormat: 'HH:mm',
              eventTimeRangeFormat: ({ start }, culture, loc) =>
                loc.format(start, 'HH:mm', culture),
              agendaTimeFormat: 'HH:mm',
              dayHeaderFormat: 'dd D. MMMM',
              dayRangeHeaderFormat: ({ start, end }, culture, loc) =>
                `${loc.format(start, 'D. MMMM', culture)} – ${loc.format(end, 'D. MMMM YYYY', culture)}`,
              dayFormat: 'dd D. M.',
              dateFormat: 'D',
              weekdayFormat: 'dd',
              monthHeaderFormat: 'MMMM YYYY',
            }}
            culture="sk"
            onSelectEvent={handleEventClick}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventDrop}
            resizable={dragAndDropEnabled}
            draggableAccessor={() => dragAndDropEnabled}
            tooltipAccessor={null}
          />
        </div>

        <div className="cal-footer">
          <Input.Password
            className="cal-admin-input"
            placeholder="prístupové heslo pre správcu"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <CalendarLegend
            total={totalCounts}
            period={periodCounts}
            periodLabel={viewRange.label}
          />
        </div>

        <Modal
          className="cal-more-modal"
          rootClassName="cal-more-modal-root"
          title={dayModal.date ? moment(dayModal.date).format('dddd D. MMMM YYYY') : 'Udalosti'}
          open={dayModal.open}
          onCancel={() => setDayModal({ open: false, date: null, events: [] })}
          footer={null}
          centered
          width={520}
        >
          <div className="cal-more-list">
            {dayModal.events.length === 0 ? (
              <div className="cal-more-empty">Žiadne udalosti</div>
            ) : (
              dayModal.events.map((event) => {
                const meta = ACTIVITY[event.activityType] || ACTIVITY.requested;
                return (
                  <button
                    key={event.id}
                    type="button"
                    className={`cal-more-item cal-more-item--${event.activityType}`}
                    onClick={() => handleEventClick(event)}
                  >
                    <span className="cal-event-mark" aria-hidden>
                      {event.activityType === 'resolved' ? '✓' : '＋'}
                    </span>
                    <span className="cal-more-item-body">
                      <span className="cal-more-item-top">
                        <span className="cal-event-id">{event.niceid}</span>
                        <span className="cal-more-item-type" style={{ color: meta.color }}>{meta.label}</span>
                        <span className="cal-more-item-time">{moment(event.start).format('HH:mm')}</span>
                      </span>
                      <span className="cal-more-item-text">{event.text}</span>
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </Modal>

        <IncidentModal
          open={modalOpen}
          incident={selectedIncident}
          onClose={handleCloseModal}
        />
      </Layout>
    </DndProvider>
  );
};

export default Dashboard;
